import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import i18n from 'i18next'

import authService from './authService'

const getUserFromLocalStorage = () => {
  const userStr = localStorage.getItem('user')
  if (userStr && userStr !== 'undefined') {
    try {
      const user = JSON.parse(userStr)
      // Sync language from stored user preference on page refresh
      // Use setTimeout to ensure i18n is initialized first
      if (user?.lang) {
        setTimeout(() => {
          i18n.changeLanguage(user.lang)
        }, 0)
      }
      return user
    } catch {
      return null
    }
  }
  return null
}

const initialState = {
  user: getUserFromLocalStorage(),
  temUser: null,
  // 2FA state
  twoFARequired: false,
  twoFAType: null as 'sms' | 'authenticator' | null,
  twoFASessionToken: null as string | null,
  isVerifying2FA: false,
  resendingCode: false,
  // Login state
  loginLoading: false,
  loginError: null as string | null,
  supports: [
    {
      id: '1',
      name: 'phone',
      create_timestamp: '2022-09-02T12:24:16.645Z',
      update_timestamp: '2023-02-07T15:16:10.026Z',
      value: {
        fr: '+41 78 205 87 89',
        en: '+41 78 205 87 89',
        de: '+41 78 242 75 33',
        it: '+41 78 205 87 89',
      },
    },
    {
      id: '2',
      name: 'whatsapp',
      create_timestamp: '2022-09-02T12:24:16.646Z',
      update_timestamp: '2023-02-07T15:16:10.085Z',
      value: {
        en: '+41 78 205 87 89',
        fr: '+41 78 205 87 89',
        de: '+41 78 205 87 89',
        it: '+41 78 205 87 89',
      },
    },
    {
      id: '3',
      name: 'email',
      create_timestamp: '2022-09-02T12:24:16.651Z',
      update_timestamp: '2023-02-07T15:16:10.168Z',
      value: {
        en: 'support@popety.com',
        fr: 'support@popety.com',
        de: 'support@popety.com',
        it: 'support@popety.com',
      },
    },
    {
      id: '4',
      name: 'openingHours',
      create_timestamp: '2022-09-02T12:24:16.626Z',
      update_timestamp: '2023-02-07T15:16:10.258Z',
      value: {
        fr: 'Lun-Ven 9h-18h',
        en: 'Mon-Fri 9h-18h',
        de: 'Mo-Fr 9.00-18.00 Uhr',
        it: 'Lun-Ven 9am-6pm',
      },
    },
  ],
  isLoading: false,
  isLoggedIn: !!localStorage.getItem('jwt'),
  needVerify: false,
  closedConfirmPhone: localStorage.getItem('closedConfirmPhone') === 'true',
  logged: false,
}

export const login = createAsyncThunk('auth/login', authService.login)

export const getPublicSupport = createAsyncThunk(
  'auth/getPublicSupport',
  authService.getPublicSupport
)

export const getSupports = createAsyncThunk(
  'auth/getSupports',
  authService.getSupports
)

// const handleLoginFulfilled = (state, action) => {
//   const { data, errors } = action.payload || {}

//   state.needVerify = false
//   if (data) {
//     state.user = data.user
//     const userLandDev = data.user?.roles.includes('User_landdev')

//     if (userLandDev) {
//       state.user = {
//         ...state.user,
//         app: userLandDev ? 'landdev' : 'brokerinfo',
//       }
//     }

//     state.logged = true
//   }

//   if (errors) {
//     state.user = null
//     state.logged = false

//     state.user = { ...state.user, app: 'brokerinfo' }
//   }
// }

const auth = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    changeApp: (state, action) => {
      state.user = { ...state.user, app: action.payload }
      localStorage.setItem('user', JSON.stringify(state.user))
    },
    forgetAuthUser: (state) => {
      state.user = null
      state.isLoggedIn = false

      localStorage.removeItem('jwt')
      localStorage.removeItem('user')
    },
    updateLocalUser: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
        localStorage.setItem('user', JSON.stringify(state.user))
      }
    },
    logout: () => {
      localStorage.removeItem('jwt')
      localStorage.removeItem('user')
      localStorage.removeItem('user-details')
      localStorage.removeItem('enabled2Fa')
      localStorage.removeItem('closedConfirmPhone')
      // TODO: From here we can take action only at this "auth" state
      // But, as we have taken care of this particular "logout" action
      // in rootReducer, we can use it to CLEAR the complete Redux Store's state
    },

    setCloseConfirmPhone: (state, { payload }) => {
      state.closedConfirmPhone = payload
      if (payload) {
        localStorage.setItem('closedConfirmPhone', 'true')
      } else {
        localStorage.removeItem('closedConfirmPhone')
      }
    },

    // 2FA reducers
    set2FAChallenge: (state, action) => {
      state.twoFARequired = action.payload.requiresTwoFactor
      state.twoFAType = action.payload.type || null
      state.twoFASessionToken = action.payload.sessionToken || null
      state.isVerifying2FA = false
      state.resendingCode = false
    },

    clear2FAChallenge: (state) => {
      state.twoFARequired = false
      state.twoFAType = null
      state.twoFASessionToken = null
      state.isVerifying2FA = false
      state.resendingCode = false
    },

    setVerifying2FA: (state, action) => {
      state.isVerifying2FA = action.payload
    },

    setResendingCode: (state, action) => {
      state.resendingCode = action.payload
    },

    clearLoginError: (state) => {
      state.loginError = null
    },
  },
  extraReducers: (builder) => {
    // Handle async actions here, not in reducers
    builder
      .addCase(login.pending, (state) => {
        state.loginLoading = true
        state.loginError = null
      })
      .addCase(login.rejected, (state, action) => {
        state.loginLoading = false
        state.loginError =
          (action.payload as string) ||
          action.error?.message ||
          'An error occurred during login'
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loginLoading = false
        const { data, errors } = action.payload || {}

        if (data) {
          // Check if 2FA is required
          if (data.requiresTwoFactor) {
            state.twoFARequired = true
            state.twoFAType = data.type
            state.twoFASessionToken = data.sessionToken
            state.isVerifying2FA = false
            state.resendingCode = false
            return
          }

          if (data.needVerify) {
            state.temUser = data.user
            state.needVerify = true
          } else {
            state.user = data.user
            const userLandDev = data.user?.roles.includes('User_landdev')

            if (userLandDev) {
              state.user = {
                ...state.user,
                app: userLandDev ? 'landdev' : 'brokerinfo',
              }
            }

            // Sync language from database to i18next
            if (data.user?.lang) {
              i18n.changeLanguage(data.user.lang)
            }

            state.logged = true
          }

          if (data.confirmedPhone) {
            localStorage.setItem('closedConfirmPhone', 'true')
          }
        }

        if (errors) {
          state.user = null
          state.logged = false
          state.loginError = errors[0]?.message || 'Invalid email or password'
        }
      })
      // .addCase(verify2faCode.fulfilled, (state, action) =>
      //   handleLoginFulfilled(state, action)
      // )
      // .addCase(confirmPhone.fulfilled, (state, action) => {
      //   const { data } = action.payload || {}

      //   if (data && data?.user?.phone) {
      //     state.user = { ...state.user, phone: data?.user?.phone }
      //   }
      // })
      .addCase(getSupports.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getSupports.fulfilled, (state, action) => {
        const { data } = action.payload || {}

        state.isLoading = false
        if (data) state.supports = data
      })
      .addCase(getSupports.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(getPublicSupport.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPublicSupport.fulfilled, (state, action) => {
        const { data } = action.payload || {}

        state.isLoading = false
        if (data) state.supports = data
      })
      .addCase(getPublicSupport.rejected, (state) => {
        state.isLoading = false
      })
  },
})

// Action creators are generated for each case reducer function
export const {
  forgetAuthUser,
  changeApp,
  updateLocalUser,
  logout,
  setCloseConfirmPhone,
  set2FAChallenge,
  clear2FAChallenge,
  setVerifying2FA,
  setResendingCode,
  clearLoginError,
} = auth.actions

export const authReducer = {
  auth: auth.reducer,
}
